import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import MultiSelect from '../MultiSelect.jsx';

export const PROJECT_STATUSES = ['lead', 'active', 'on_hold', 'completed', 'cancelled'];
export const COUNTRIES = ['Italy', 'Morocco', 'UAE', 'Other'];
export const blankProject = {
  name: '', status: 'lead', business_line: 'trade', country: 'Italy',
  parent_id: '', own_company_id: '',
  category_id: '', exclusivity_id: '',
  gross_value: '', net_encaissed: '', commission_pct_override: '',
  deadline: '', description: '',
  client_ids: [], manufacturer_ids: [],
};

/**
 * Create/edit a project. Trade projects carry the full deal apparatus
 * (clients, manufacturers, exclusivity, commission financials); internal
 * initiatives (Dubai entity setup, CFC label application…) have none of
 * that, so those fields are hidden rather than left blank and confusing.
 */
export default function ProjectForm({ initial, companies = [], isAdmin = false, onClose, onSaved, onDeleted }) {
  const confirm = useConfirm();
  const toast = useToast();
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...blankProject, ...initial });
  const isInternal = form.business_line === 'internal';
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [types, setTypes] = useState([]);
  const [exclusivities, setExclusivities] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const clients = companies.filter((c) => c.type === 'client');
  const manufacturers = companies.filter((c) => c.type === 'manufacturer');
  const ownCompanies = companies.filter((c) => c.type === 'own');
  const parentOptions = allProjects.filter((p) => p.id !== initial.id);

  useEffect(() => {
    api.get('/categories?domain=project_type').then((r) => setTypes(r.data)).catch(() => {});
    api.get('/exclusivities').then((r) => setExclusivities(r.data)).catch(() => {});
    api.get('/projects').then((r) => setAllProjects(r.data)).catch(() => {});
    // On edit, pull the full record (arrays of linked ids aren't in list rows).
    if (!isNew) {
      api.get(`/projects/${initial.id}`).then((r) => {
        const p = r.data;
        setForm((f) => ({
          ...f,
          name: p.name ?? f.name,
          status: p.status ?? f.status,
          business_line: p.business_line ?? f.business_line,
          country: p.country ?? f.country,
          parent_id: p.parent_id ?? '',
          own_company_id: p.own_company_id ?? '',
          category_id: p.category_id ?? '',
          exclusivity_id: p.exclusivity_id ?? '',
          gross_value: p.gross_value ?? '',
          net_encaissed: p.net_encaissed ?? '',
          commission_pct_override: p.commission_pct_override ?? '',
          deadline: p.deadline ?? '',
          description: p.description ?? '',
          client_ids: p.client_ids ?? [],
          manufacturer_ids: p.manufacturer_ids ?? [],
        }));
      }).catch(() => {});
    }
  }, [initial.id, isNew]);

  // Live commission preview.
  const selectedExcl = exclusivities.find((e) => Number(e.id) === Number(form.exclusivity_id));
  const pct = form.commission_pct_override !== '' && form.commission_pct_override !== null
    ? Number(form.commission_pct_override)
    : (selectedExcl?.commission_pct != null ? Number(selectedExcl.commission_pct) : 0);
  const net = Number(form.net_encaissed) || 0;
  const commission = Math.round(net * pct) / 100;

  async function save() {
    setBusy(true); setErrors({});
    try {
      const payload = { ...form };
      if (isNew) await api.post('/projects', payload);
      else await api.put(`/projects/${initial.id}`, payload);
      toast(isNew ? t('Project created') : t('Project saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete project'), message: t('Delete this project and everything linked to it (contracts, tasks, files)?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/projects/${initial.id}`);
    toast(t('Project deleted'));
    (onDeleted || onSaved)();
  }

  return (
    <Modal
      title={isNew
        ? (isInternal ? t('New initiative') : t('New project'))
        : (isInternal ? t('Edit initiative') : t('Edit project'))}
      onClose={onClose}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Name')} error={errors.name}><input value={form.name} onChange={set('name')} /></Field>

      <div className="grid-3">
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{PROJECT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
        <Field label={t('Country')}><select value={form.country} onChange={set('country')}>{COUNTRIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}</select></Field>
        <Field label={t('Type')}>
          <select value={form.category_id || ''} onChange={set('category_id')}>
            <option value="">—</option>
            {types.map((ty) => <option key={ty.id} value={ty.id}>{ty.name}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid-2">
        <Field label={t('Our company')}>
          <select value={form.own_company_id || ''} onChange={set('own_company_id')}>
            <option value="">—</option>
            {ownCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label={t('Parent project (for a sub-project)')}>
          <select value={form.parent_id || ''} onChange={set('parent_id')}>
            <option value="">{t('— None (top-level) —')}</option>
            {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>

      {/* Deal apparatus — trade only. An internal initiative (company setup,
          label application) has no clients, manufacturers or commission. */}
      {!isInternal && (
        <>
          <MultiSelect label={t('Clients')} options={clients} value={form.client_ids}
            onChange={(ids) => setForm((f) => ({ ...f, client_ids: ids }))} empty={t('No client companies')} />
          <MultiSelect label={t('Manufacturers')} options={manufacturers} value={form.manufacturer_ids}
            onChange={(ids) => setForm((f) => ({ ...f, manufacturer_ids: ids }))} empty={t('No manufacturer companies')} />

          <Field label={t('Exclusivity')}>
            <select value={form.exclusivity_id || ''} onChange={set('exclusivity_id')}>
              <option value="">{t('— None —')}</option>
              {exclusivities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}{e.commission_pct != null ? ` (${e.commission_pct}%)` : ''}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid-3">
            <Field label={t('Gross value (€)')}><input type="number" step="0.01" value={form.gross_value ?? ''} onChange={set('gross_value')} /></Field>
            <Field label={t('Net collected (€)')}><input type="number" step="0.01" value={form.net_encaissed ?? ''} onChange={set('net_encaissed')} /></Field>
            <Field label={t('Commission % override')}><input type="number" step="0.01" placeholder={selectedExcl?.commission_pct ?? ''} value={form.commission_pct_override ?? ''} onChange={set('commission_pct_override')} /></Field>
          </div>

          <div className="commission-preview">
            {t('Commission')}: <b>€{commission.toLocaleString()}</b>
            <span className="muted sm"> &nbsp;({t('{pct}% of net', { pct })}{form.commission_pct_override !== '' && form.commission_pct_override !== null ? `, ${t('override')}` : selectedExcl ? `, ${t('from exclusivity')}` : ''})</span>
          </div>
        </>
      )}

      <div className="grid-2">
        <Field label={t('Deadline')} error={errors.deadline}><input type="date" value={form.deadline || ''} onChange={set('deadline')} /></Field>
      </div>
      <Field label={t('Description')}><textarea rows={3} value={form.description || ''} onChange={set('description')} /></Field>
    </Modal>
  );
}
